import { useMemo, useState } from 'react';
import { useUITranslate } from '../../../hooks/useUITranslate';
import { CreationLayoutBody } from './Body/Body';
import { CreationLayoutFooter } from './Footer/Footer';
import { CreationLayoutHeader } from './Header/Header';

export type TUseCreationLayoutProps = {
  showStepper?: boolean;
  totalSteps?: number;
  stepsName?: { label: string }[];
  steps?: React.ReactNode[];
  onSubmitStep?: () => Promise<void> | void;
  onNextStep?: (updateStep: () => void) => Promise<void> | void;
  showTab?: boolean;
  totalTabs?: number;
  tabsName?: { label: string }[];
  tabs?: React.ReactNode[];
  showClose?: boolean;
  onClose?: () => void;
} & (
  | {
      showStepper: true;
      totalSteps: number;
      stepsName?: { label: string }[];
      steps?: React.ReactNode[];
      onSubmitStep: () => Promise<void> | void;
      onNextStep: (updateStep: () => void) => Promise<void> | void;
    }
  | {
      showStepper?: false;
      totalSteps?: number;
      stepsName?: { label: string }[];
      steps?: React.ReactNode[];
      onSubmitStep?: () => Promise<void> | void;
      onNextStep?: (updateStep: () => void) => Promise<void> | void;
    }
) &
  (
    | {
        showTab: true;
        totalTabs: number;
        tabsName?: { label: string }[];
        tabs?: React.ReactNode[];
      }
    | {
        showTab?: false;
        totalTabs?: number;
        tabsName?: { label: string }[];
        tabs?: React.ReactNode[];
      }
  ) &
  (
    | {
        showClose: true;
        onClose: () => void;
      }
    | {
        showClose?: false;
        onClose?: () => void;
      }
  );

export const useCreationLayout = (props: TUseCreationLayoutProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [currentTab, setCurrentTab] = useState(0);
  const { translate } = useUITranslate();

  // Header Config
  const stepsName =
    props.stepsName && props.stepsName.length === props.totalSteps
      ? props.stepsName
      : Array.from({ length: props?.totalSteps ?? 0 }).map((_, index) => {
          return { label: `${translate('Step')} ${index + 1}` };
        }) ?? [];

  const tabsName =
    props.tabsName && props.tabsName.length === props.totalTabs
      ? props.tabsName
      : Array.from({ length: props?.totalTabs ?? 0 }).map((_, index) => {
          return { label: `${translate('Tab')} ${index + 1}` };
        }) ?? [];

  // Footer Config
  const nextButtonText = useMemo(
    () =>
      !props.totalSteps
        ? ''
        : currentStep === props?.totalSteps - 1
        ? translate('Submit')
        : translate('Next'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep],
  );

  const nextButtonOnClick = async () => {
    if (props.showStepper) {
      if (currentStep !== props.totalSteps - 1) {
        props.onNextStep(() => {
          setCurrentStep(currentStep + 1);
        });
      } else {
        await props.onSubmitStep();
      }
    }
  };

  const backButtonText = useMemo(
    () => (!props.totalSteps || currentStep === 0 ? null : translate('Back')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep],
  );

  // Body Config
  const bodyChildren =
    props.showTab && props.tabs
      ? props.tabs[currentTab]
      : props.showStepper && props.steps
      ? props.steps[currentStep]
      : null;

  return {
    headerConfig: {
      // Stepper
      showStepper: props.showStepper,
      currentStep,
      stepsName,
      // Tab
      showTab: props.showTab,
      tabsName,
      currentTab,
      onChangeTab: val => setCurrentTab(val),
      showClose: props.showClose,
      onClose: props.onClose,
    } as Partial<CreationLayoutHeader>,
    footerConfig: {
      nextButtonText,
      nextButtonOnClick,
      backButtonText,
      backButtonOnClick: () => setCurrentStep(currentStep - 1),
    } as Partial<CreationLayoutFooter>,
    bodyConfig: {
      children: bodyChildren,
    } as Partial<CreationLayoutBody>,
    currentStep,
    currentTab,
  };
};
